import { styled } from '@automata/mui-theme';
import { useFlag } from '@automata/utils/hooks';
import { useCallback, useRef, useState } from 'react';
import ReactFlow, {
  ConnectionMode,
  Controls,
  ReactFlowInstance,
  SelectionMode,
} from 'reactflow';

import 'reactflow/dist/style.css';
import { edgeTypes } from './edges';
import { useCanvasClickHandler } from './hooks/useCanvasClickHandler';
import { useCanvasHandlers } from './hooks/useCanvasHandlers';
import { useCanvasSelectionHandler } from './hooks/useCanvasSelectionHandler';
import { useConnectionHandler } from './hooks/useConnectionHandler';
import { useResizeHelper } from './hooks/useResizeHelper';
import { nodeTypes } from './utils';
import { WFBConnectionLine } from './WFBConnectionLine';
import useAutoLayout from '../../hooks/useAutoLayout';
import useKeyboardShortcuts from '../../hooks/useKeyboardShortcuts';
import useKeysCurrentlyDown from '../../hooks/useKeysCurrentlyDown';
import { isMacOS } from '../../utils';

const ReactFlowStyled = styled(ReactFlow)`
  /* Hide outline when panel is focused */
  &:focus {
    outline: none;
  }

  & .react-flow__nodesselection {
    display: none;
  }

  & .react-flow__handle.connecting {
    cursor: not-allowed;
  }

  & .react-flow__handle.valid {
    cursor: crosshair;
  }
`;

const AutoLayout = (): JSX.Element | null => {
  const spacing = 175;
  useAutoLayout({
    algorithm: 'dagre',
    direction: 'TB',
    spacing: [spacing, spacing],
  });

  return null;
};

export const GraphEditor = () => {
  const [isTrackpad, setIsTrackpad] = useState(false);

  const reactFlowRef = useRef<HTMLDivElement>(null);

  const instanceRef = useRef<ReactFlowInstance | null>(null);

  const { handleViewPositionChange } = useResizeHelper(
    reactFlowRef,
    instanceRef
  );

  const sdkInteropFlagEnabled = useFlag('fe.canvas_sdk_interoperability');

  useKeyboardShortcuts(reactFlowRef);

  const { transientEdges, transientNodes, ...canvasHandlers } =
    useCanvasHandlers();

  const keysCurrentlyDown = useKeysCurrentlyDown();

  const { handleSelectionDrag } = useCanvasSelectionHandler();

  const { handleGraphEditorClick } = useCanvasClickHandler();

  const { isValidConnection } = useConnectionHandler();

  const handleInit = useCallback(
    (instance: ReactFlowInstance) => {
      instanceRef.current = instance;
      instance.fitView({ maxZoom: 1, minZoom: 0 });
      handleViewPositionChange(); // initialize position
    },
    [handleViewPositionChange]
  );

  return (
    <ReactFlowStyled
      {...{
        ...canvasHandlers,
        edges: transientEdges,
        edgeTypes,
        isValidConnection,
        nodeTypes,
      }}
      ref={reactFlowRef}
      onInit={handleInit}
      onSelectionStart={() => handleSelectionDrag(true)}
      onSelectionEnd={() => handleSelectionDrag(false)}
      connectionLineComponent={WFBConnectionLine}
      connectionMode={ConnectionMode.Loose}
      deleteKeyCode={
        null /* deletion is handled in Redux-land, not in ReactFlow */
      }
      maxZoom={1.5}
      minZoom={0.05}
      tabIndex={-1}
      // multiSelectionKeyCode is for allowing clicking on items to select/deselect those items
      multiSelectionKeyCode={[isMacOS() ? 'Meta' : 'Control', 'Shift']}
      nodeOrigin={[0, 1]}
      nodes={transientNodes}
      onClick={handleGraphEditorClick}
      onMoveEnd={handleViewPositionChange}
      proOptions={{ hideAttribution: true }}
      selectionKeyCode={null}
      selectionMode={SelectionMode.Partial}
      selectionOnDrag
      panActivationKeyCode={['Alt', 'Shift']}
      panOnDrag={[1, 2]} // pan with middle button (1) and right button (2)
      zoomOnScroll={!isTrackpad}
      panOnScroll={isTrackpad}
      onMoveStart={(e) => {
        if (isTrackpad) return; // trackpad mode is only activated once
        if (keysCurrentlyDown.includes('Shift')) return; // this prevents trackpad mode being activated when holding shift and using mousewheel
        if (!(e instanceof WheelEvent)) return; // this can also be instance of MouseEvent
        if (e.deltaX !== 0) setIsTrackpad(true);
      }}
    >
      <Controls position="bottom-right" />
      {sdkInteropFlagEnabled && <AutoLayout />}
      {/* TODO(IL, 2024-03-12): need to custom style controls and minimap 
      so they don't overlap each other, probably with Panel component 
      https://reactflow.dev/api-reference/components/panel */}
      {/* <MiniMap pannable zoomable /> */}
    </ReactFlowStyled>
  );
};
